import type { Control, FieldPath, FieldValues } from "react-hook-form";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import { FormTooltip } from "@/components/ui/form-description";

interface RadioGroupOption {
  value: string;
  label: string;
}

interface RadioGroupFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends React.HTMLAttributes<HTMLDivElement> {
  control: Control<TFieldValues>;
  name: TName;
  label: string;
  description?: string;
  descriptionAsTooltip?: boolean;
  required?: boolean;
  requiredLabel?: string;
  options: readonly RadioGroupOption[];
  layout?: "vertical" | "horizontal";
  highContrast?: boolean;
}

export function RadioGroupField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  description,
  descriptionAsTooltip = false,
  required = false,
  requiredLabel = "Required",
  options,
  layout = "vertical",
  highContrast = false,
  className,
  ...htmlProps
}: RadioGroupFieldProps<TFieldValues, TName>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className} {...htmlProps}>
          <div className="mb-4">
            <FormLabel className="inline-block text-base leading-snug">
              {label}
              {required && (
                <>
                  <span className="ms-1 text-red-500" aria-hidden="true">
                    *
                  </span>
                  <span className="sr-only">({requiredLabel})</span>
                </>
              )}
              {description && descriptionAsTooltip && (
                <FormTooltip description={description} />
              )}
            </FormLabel>
            {description && !descriptionAsTooltip && (
              <FormDescription>{description}</FormDescription>
            )}
          </div>
          <FormControl>
            <RadioGroup
              onValueChange={field.onChange}
              defaultValue={field.value}
              className={cn(
                "flex gap-4",
                layout === "vertical" ? "flex-col" : "flex-row flex-wrap",
              )}
            >
              {options.map((option) => (
                <FormItem
                  key={option.value}
                  className="flex items-center gap-3"
                >
                  <FormControl>
                    <RadioGroupItem
                      value={option.value}
                      className={cn(
                        highContrast && "border-1 border-neutral-600",
                      )}
                    />
                  </FormControl>
                  <FormLabel className="leading-snug font-normal">
                    {option.label}
                  </FormLabel>
                </FormItem>
              ))}
            </RadioGroup>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
